import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectsStore } from '../store/projects';
import { usePostsStore, Post, PostStatus } from '../store/posts';
import { useAuthStore, PostListPreferences } from '../store/auth';
import { useFlightsStore } from '../store/flights';
import { PostCalendar, CalendarView, CalendarAction } from '../components/PostCalendar';
import { PostDetailModal } from '../components/PostDetailModal';
import { CreatePostModal, CreatePostData } from '../components/CreatePostModal';
import { BatchUpload } from '../components/BatchUpload';
import { BatchHistory } from '../components/BatchHistory';
import { FlightsList } from '../components/FlightsList';
import { PageHeader } from '../components/PageHeader';
import { ReportGeneratorModal } from '../components/ReportGeneratorModal';
import { PostType } from '../config/formatSpecs';

// Post-Type Labels und Icons für Anzeige
const POST_TYPE_ICONS: Record<PostType, string> = {
  feed: '📄',
  reel: '🎬',
  story: '📲',
  carousel: '🎠',
};

const STATUS_COLORS: Record<PostStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  publishing: 'bg-yellow-100 text-yellow-800',
  published: 'bg-green-100 text-green-800',
  partial: 'bg-orange-100 text-orange-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

// Gruppierungs-Optionen
type GroupBy = 'none' | 'date' | 'week' | 'month' | 'status' | 'platform';

function PostCard({
  post,
  onDelete,
  onCancel,
  onClick,
  isExpanded,
  onToggleExpand,
  isSelected,
  onToggleSelect,
  selectionMode,
  t,
}: {
  post: Post;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
  onClick: (post: Post) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isSelected: boolean;
  onToggleSelect: () => void;
  selectionMode: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [showActions, setShowActions] = useState(false);

  const canDelete = ['draft', 'scheduled', 'failed', 'cancelled'].includes(post.status);
  const canCancel = post.status === 'scheduled';
  const hasLongText = post.text.length > 150;

  return (
    <div
      className={`bg-white rounded-lg shadow p-4 relative cursor-pointer hover:shadow-md transition-all ${
        isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
      }`}
      onClick={() => selectionMode ? onToggleSelect() : onClick(post)}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-2 flex-wrap items-center">
          {/* Selection Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect();
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          {/* Post-Typ Badge */}
          {post.post_type && POST_TYPE_ICONS[post.post_type] && (
            <span
              className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                post.post_type === 'feed' ? 'bg-gray-100 text-gray-700' :
                post.post_type === 'reel' ? 'bg-purple-100 text-purple-700' :
                post.post_type === 'story' ? 'bg-orange-100 text-orange-700' :
                'bg-teal-100 text-teal-700'
              }`}
              title={t(`projects:postType.${post.post_type}`)}
            >
              <span>{POST_TYPE_ICONS[post.post_type]}</span>
              <span>{t(`projects:postType.${post.post_type}`)}</span>
            </span>
          )}
          {post.platforms.map((platform) => (
            <span
              key={platform}
              className={`px-2 py-0.5 text-xs rounded-full ${
                platform === 'facebook' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
              }`}
            >
              {platform}
            </span>
          ))}
          <span className="text-xs text-gray-400">{post.display_id}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[post.status]}`}>
            {t(`projects:postStatus.${post.status}`)}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
        </div>
      </div>

      <p className={`text-gray-800 mb-3 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-4'}`}>
        {post.text}
      </p>

      {hasLongText && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          className="text-xs text-blue-600 hover:text-blue-800 mb-2"
        >
          {isExpanded ? t('projects:postList.showLess') : t('projects:postList.showMore')}
        </button>
      )}

      {/* Media Preview */}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className="flex gap-1 mb-2 overflow-hidden">
          {post.media_urls.slice(0, 3).map((url, idx) => (
            <div key={idx} className="w-12 h-12 rounded bg-gray-100 overflow-hidden flex-shrink-0">
              <img src={url} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
          {post.media_urls.length > 3 && (
            <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
              +{post.media_urls.length - 3}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between items-center text-sm text-gray-500">
        <span>
          {new Date(post.scheduled_for).toLocaleDateString('de-DE', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        {post.error_message && (
          <span className="text-red-500 text-xs truncate max-w-[200px]" title={post.error_message}>
            {post.error_message}
          </span>
        )}
      </div>

      {showActions && (
        <div className="absolute right-2 top-12 bg-white border rounded-lg shadow-lg py-1 z-10" onClick={(e) => e.stopPropagation()}>
          {canCancel && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCancel(post.id);
                setShowActions(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              {t('common:cancel')}
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(post.id);
                setShowActions(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
            >
              {t('common:delete')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Hilfsfunktion für Gruppierung
function groupPosts(posts: Post[], groupBy: GroupBy, t: (key: string) => string): Map<string, Post[]> {
  const groups = new Map<string, Post[]>();

  if (groupBy === 'none') {
    groups.set('all', posts);
    return groups;
  }

  for (const post of posts) {
    let key: string;
    const date = new Date(post.scheduled_for);

    switch (groupBy) {
      case 'date':
        key = date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
        break;
      case 'week': {
        // ISO week number
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
        const weekNum = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
        key = `KW ${weekNum} / ${date.getFullYear()}`;
        break;
      }
      case 'month':
        key = date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
        break;
      case 'status':
        key = t(`projects:postStatus.${post.status}`);
        break;
      case 'platform':
        key = post.platforms.join(' & ');
        break;
      default:
        key = t('projects:postList.other');
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(post);
  }

  return groups;
}

// ListView-Komponente mit Suche, Filter, Gruppierung
function PostListView({
  posts,
  statusFilter,
  onStatusFilterChange,
  onDelete,
  onCancel,
  onClick,
  onBatchUpdate,
  onBatchDelete,
  preferences,
  onPreferencesChange,
}: {
  posts: Post[];
  statusFilter: PostStatus | 'all';
  onStatusFilterChange: (status: PostStatus | 'all') => void;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
  onClick: (post: Post) => void;
  onBatchUpdate: (postIds: string[], updates: { status?: 'draft' | 'scheduled'; platforms?: string[]; post_type?: PostType }) => Promise<void>;
  onBatchDelete: (postIds: string[]) => Promise<void>;
  preferences: {
    platformFilter: 'all' | 'facebook' | 'instagram';
    postTypeFilter: 'all' | PostType;
    groupBy: GroupBy;
    expandAll: boolean;
  };
  onPreferencesChange: (prefs: Partial<{
    platformFilter: 'all' | 'facebook' | 'instagram';
    postTypeFilter: 'all' | PostType;
    groupBy: GroupBy;
    expandAll: boolean;
  }>) => void;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>(preferences.groupBy);
  const [expandAll, setExpandAll] = useState(preferences.expandAll);
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());
  const [platformFilter, setPlatformFilter] = useState<'all' | 'facebook' | 'instagram'>(preferences.platformFilter);
  const [postTypeFilter, setPostTypeFilter] = useState<'all' | PostType>(preferences.postTypeFilter);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // Clear selection when filters change (to avoid hidden selected posts)
  useEffect(() => {
    setSelectedPosts(new Set());
  }, [statusFilter, platformFilter, postTypeFilter, searchQuery, dateFrom, dateTo]);

  // Filter posts
  const filteredPosts = useMemo(() => {
    let result = posts;

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter((p) => p.status === statusFilter);
    }

    // Platform filter
    if (platformFilter !== 'all') {
      result = result.filter((p) => p.platforms.includes(platformFilter));
    }

    // Post-Type filter
    if (postTypeFilter !== 'all') {
      result = result.filter((p) => p.post_type === postTypeFilter);
    }

    // Date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((p) => new Date(p.scheduled_for) >= fromDate);
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((p) => new Date(p.scheduled_for) <= toDate);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((p) =>
        p.text.toLowerCase().includes(query) ||
        p.display_id.toLowerCase().includes(query)
      );
    }

    // Sort by scheduled_for
    result = [...result].sort((a, b) =>
      new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime()
    );

    return result;
  }, [posts, statusFilter, platformFilter, postTypeFilter, searchQuery, dateFrom, dateTo]);

  // Group posts
  const groupedPosts = useMemo(() => groupPosts(filteredPosts, groupBy, t), [filteredPosts, groupBy, t]);

  const toggleExpand = (postId: string) => {
    setExpandedPosts((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const isPostExpanded = (postId: string) => expandAll || expandedPosts.has(postId);

  // Selection functions
  const toggleSelect = (postId: string) => {
    setSelectedPosts((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedPosts(new Set(filteredPosts.map((p) => p.id)));
  };

  const deselectAll = () => {
    setSelectedPosts(new Set());
  };

  const selectionMode = selectedPosts.size > 0;

  // Check if filters are active
  const hasActiveFilters = statusFilter !== 'all' || platformFilter !== 'all' || postTypeFilter !== 'all' || searchQuery.trim() !== '' || dateFrom !== '' || dateTo !== '';

  // Batch action handlers
  const handleBatchStatusChange = async (status: 'draft' | 'scheduled') => {
    if (selectedPosts.size === 0) return;
    setIsBatchProcessing(true);
    try {
      await onBatchUpdate(Array.from(selectedPosts), { status });
      deselectAll();
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleBatchPlatformChange = async (platforms: string[]) => {
    if (selectedPosts.size === 0) return;
    setIsBatchProcessing(true);
    try {
      await onBatchUpdate(Array.from(selectedPosts), { platforms });
      deselectAll();
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleBatchPostTypeChange = async (post_type: PostType) => {
    if (selectedPosts.size === 0) return;
    setIsBatchProcessing(true);
    try {
      await onBatchUpdate(Array.from(selectedPosts), { post_type });
      deselectAll();
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedPosts.size === 0) return;
    if (!confirm(t('projects:batch.deleteConfirm', { count: selectedPosts.size }))) return;
    setIsBatchProcessing(true);
    try {
      await onBatchDelete(Array.from(selectedPosts));
      deselectAll();
    } finally {
      setIsBatchProcessing(false);
    }
  };

  // Stats
  const stats = useMemo(() => ({
    total: filteredPosts.length,
    draft: filteredPosts.filter((p) => p.status === 'draft').length,
    scheduled: filteredPosts.filter((p) => p.status === 'scheduled').length,
    published: filteredPosts.filter((p) => p.status === 'published').length,
    failed: filteredPosts.filter((p) => p.status === 'failed').length,
  }), [filteredPosts]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('projects:postList.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as PostStatus | 'all')}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
          >
            <option value="all">{t('projects:postList.allStatus')} ({posts.length})</option>
            <option value="draft">{t('projects:postList.drafts')}</option>
            <option value="scheduled">{t('projects:postStatus.scheduled')}</option>
            <option value="published">{t('projects:postStatus.published')}</option>
            <option value="failed">{t('projects:postStatus.failed')}</option>
            <option value="cancelled">{t('projects:postStatus.cancelled')}</option>
          </select>

          {/* Platform Filter */}
          <select
            value={platformFilter}
            onChange={(e) => {
              const value = e.target.value as 'all' | 'facebook' | 'instagram';
              setPlatformFilter(value);
              onPreferencesChange({ platformFilter: value });
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
          >
            <option value="all">{t('projects:postList.allPlatforms')}</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
          </select>

          {/* Post-Type Filter */}
          <select
            value={postTypeFilter}
            onChange={(e) => {
              const value = e.target.value as 'all' | PostType;
              setPostTypeFilter(value);
              onPreferencesChange({ postTypeFilter: value });
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
          >
            <option value="all">{t('projects:postList.allPostTypes')}</option>
            <option value="feed">📄 {t('projects:postType.feed')}</option>
            <option value="reel">🎬 {t('projects:postType.reel')}</option>
            <option value="story">📲 {t('projects:postType.story')}</option>
            <option value="carousel">🎠 {t('projects:postType.carousel')}</option>
          </select>

          {/* Group By */}
          <select
            value={groupBy}
            onChange={(e) => {
              const value = e.target.value as GroupBy;
              setGroupBy(value);
              onPreferencesChange({ groupBy: value });
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
          >
            <option value="none">{t('projects:groupBy.none')}</option>
            <option value="date">{t('projects:groupBy.date')}</option>
            <option value="week">{t('projects:groupBy.week')}</option>
            <option value="month">{t('projects:groupBy.month')}</option>
            <option value="status">{t('projects:groupBy.status')}</option>
            <option value="platform">{t('projects:groupBy.platform')}</option>
          </select>

          {/* Date Range Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">{t('projects:postList.dateFrom')}</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
            />
            <span className="text-xs text-gray-500">{t('projects:postList.dateTo')}</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-gray-400 hover:text-gray-600"
                title={t('projects:postList.clearDateFilter')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex-1" />

          {/* Selection Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={selectedPosts.size === filteredPosts.length ? deselectAll : selectAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {selectedPosts.size === filteredPosts.length ? t('projects:postList.deselectAll') : t('projects:postList.selectAll')}
            </button>
            {selectedPosts.size > 0 && (
              <span className="text-xs text-gray-500">
                ({selectedPosts.size} {t('projects:postList.selected')})
              </span>
            )}
          </div>

          {/* Expand All Toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={expandAll}
              onChange={(e) => {
                setExpandAll(e.target.checked);
                onPreferencesChange({ expandAll: e.target.checked });
              }}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            {t('projects:postList.expandAll')}
          </label>
        </div>

        {/* Stats Bar */}
        <div className="flex gap-4 text-xs text-gray-500 border-t pt-3 items-center">
          <span>
            {hasActiveFilters ? (
              <>
                <span className="font-medium text-blue-600">{stats.total}</span> {t('projects:postList.of')} {posts.length} {t('projects:detail.posts')}
              </>
            ) : (
              <>{stats.total} {t('projects:detail.posts')}</>
            )}
          </span>
          {stats.draft > 0 && <span className="text-gray-600">{stats.draft} {t('projects:postList.drafts')}</span>}
          {stats.scheduled > 0 && <span className="text-blue-600">{stats.scheduled} {t('projects:postStatus.scheduled')}</span>}
          {stats.published > 0 && <span className="text-green-600">{stats.published} {t('projects:postStatus.published')}</span>}
          {stats.failed > 0 && <span className="text-red-600">{stats.failed} {t('projects:postStatus.failed')}</span>}

          {hasActiveFilters && (
            <>
              <div className="flex-1" />
              <button
                onClick={() => {
                  onStatusFilterChange('all');
                  setPlatformFilter('all');
                  setPostTypeFilter('all');
                  setSearchQuery('');
                  setDateFrom('');
                  setDateTo('');
                  onPreferencesChange({
                    platformFilter: 'all',
                    postTypeFilter: 'all',
                  });
                }}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('projects:postList.resetFilters')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Batch Actions Toolbar */}
      {selectionMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-800">
              {t('projects:batch.postsSelected', { count: selectedPosts.size })}
            </span>
            {hasActiveFilters && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {t('projects:batch.fromFiltered', { count: filteredPosts.length })}
              </span>
            )}
            <button
              onClick={deselectAll}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              {t('projects:batch.clearSelection')}
            </button>
          </div>

          <div className="h-6 w-px bg-blue-200" />

          {/* Status ändern */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-700">{t('projects:batch.changeStatus')}</span>
            <button
              onClick={() => handleBatchStatusChange('draft')}
              disabled={isBatchProcessing}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
            >
              → {t('projects:batch.toDraft')}
            </button>
            <button
              onClick={() => handleBatchStatusChange('scheduled')}
              disabled={isBatchProcessing}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
            >
              → {t('projects:batch.toScheduled')}
            </button>
          </div>

          <div className="h-6 w-px bg-blue-200" />

          {/* Plattform ändern */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-700">{t('projects:batch.changePlatform')}</span>
            <button
              onClick={() => handleBatchPlatformChange(['facebook'])}
              disabled={isBatchProcessing}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200 disabled:opacity-50"
            >
              {t('projects:batch.fbOnly')}
            </button>
            <button
              onClick={() => handleBatchPlatformChange(['instagram'])}
              disabled={isBatchProcessing}
              className="px-3 py-1 text-sm bg-pink-100 text-pink-800 rounded hover:bg-pink-200 disabled:opacity-50"
            >
              {t('projects:batch.igOnly')}
            </button>
            <button
              onClick={() => handleBatchPlatformChange(['facebook', 'instagram'])}
              disabled={isBatchProcessing}
              className="px-3 py-1 text-sm bg-purple-100 text-purple-800 rounded hover:bg-purple-200 disabled:opacity-50"
            >
              {t('projects:batch.fbAndIg')}
            </button>
          </div>

          <div className="h-6 w-px bg-blue-200" />

          {/* Post-Typ ändern */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-700">{t('projects:batch.changeType')}</span>
            <button
              onClick={() => handleBatchPostTypeChange('feed')}
              disabled={isBatchProcessing}
              className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
              title={t('projects:batch.feedPost')}
            >
              📄
            </button>
            <button
              onClick={() => handleBatchPostTypeChange('reel')}
              disabled={isBatchProcessing}
              className="px-2 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
              title={t('projects:postType.reel')}
            >
              🎬
            </button>
            <button
              onClick={() => handleBatchPostTypeChange('story')}
              disabled={isBatchProcessing}
              className="px-2 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50"
              title={t('projects:postType.story')}
            >
              📲
            </button>
            <button
              onClick={() => handleBatchPostTypeChange('carousel')}
              disabled={isBatchProcessing}
              className="px-2 py-1 text-sm bg-teal-100 text-teal-700 rounded hover:bg-teal-200 disabled:opacity-50"
              title={t('projects:postType.carousel')}
            >
              🎠
            </button>
          </div>

          <div className="flex-1" />

          {/* Löschen */}
          <button
            onClick={handleBatchDelete}
            disabled={isBatchProcessing}
            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {t('common:delete')}
          </button>
        </div>
      )}

      {/* Posts List */}
      {filteredPosts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">
            {searchQuery ? t('projects:postList.noPostsFound') : t('projects:postList.noPosts')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(groupedPosts.entries()).map(([groupName, groupPostsList]) => (
            <div key={groupName}>
              {groupBy !== 'none' && (
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">{groupName}</h3>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {t('projects:postList.postsCount', { count: groupPostsList.length })}
                  </span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupPostsList.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onDelete={onDelete}
                    onCancel={onCancel}
                    onClick={onClick}
                    isExpanded={isPostExpanded(post.id)}
                    onToggleExpand={() => toggleExpand(post.id)}
                    isSelected={selectedPosts.has(post.id)}
                    onToggleSelect={() => toggleSelect(post.id)}
                    selectionMode={selectionMode}
                    t={t}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProjectDetailPage() {
  const { t } = useTranslation(['projects', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentProject, fetchProject, fetchProjectStats, currentStats, setSelectedProjectId } = useProjectsStore();
  const { posts, fetchPosts, createPost, updatePost, deletePost, cancelPost, duplicatePost, batchUpdate, batchDelete, isLoading, error } = usePostsStore();
  const { user, updatePreferences } = useAuthStore();
  const { flights, fetchFlights } = useFlightsStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isBatchHistoryOpen, setIsBatchHistoryOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<PostStatus | 'all'>(
    (user?.preferences?.postList?.statusFilter as PostStatus | 'all') || 'all'
  );
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [defaultScheduleDate, setDefaultScheduleDate] = useState<Date | null>(null);
  const [calendarView, setCalendarView] = useState<CalendarView>('dayGridMonth');

  // Context menu state for calendar
  const [openFlightModal, setOpenFlightModal] = useState(false);
  const [flightModalStartDate, setFlightModalStartDate] = useState<Date | undefined>(undefined);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Get user preferences with defaults
  const postListPrefs = useMemo(() => ({
    platformFilter: (user?.preferences?.postList?.platformFilter || 'all') as 'all' | 'facebook' | 'instagram',
    postTypeFilter: (user?.preferences?.postList?.postTypeFilter || 'all') as 'all' | PostType,
    groupBy: (user?.preferences?.postList?.groupBy || 'week') as GroupBy,
    expandAll: user?.preferences?.postList?.expandAll || false,
  }), [user?.preferences?.postList]);

  // Handler to update preferences
  const handlePreferencesChange = useCallback((prefs: Partial<typeof postListPrefs>) => {
    updatePreferences({
      postList: {
        ...user?.preferences?.postList,
        statusFilter: statusFilter,
        ...prefs,
      } as PostListPreferences,
    });
  }, [updatePreferences, user?.preferences?.postList, statusFilter]);

  // Update status filter in preferences when it changes
  const handleStatusFilterChange = useCallback((status: PostStatus | 'all') => {
    setStatusFilter(status);
    updatePreferences({
      postList: {
        ...user?.preferences?.postList,
        statusFilter: status,
      } as PostListPreferences,
    });
  }, [updatePreferences, user?.preferences?.postList]);

  useEffect(() => {
    if (id) {
      fetchProject(id);
      fetchProjectStats(id);
      // Load up to 500 posts for calendar year view
      fetchPosts(id, { limit: 500 });
      fetchFlights(id);
      // Set global selected project for navigation persistence
      setSelectedProjectId(id);
    }
  }, [id, fetchProject, fetchProjectStats, fetchPosts, fetchFlights, setSelectedProjectId]);

  const handleCreate = async (data: CreatePostData) => {
    await createPost(data);
    setDefaultScheduleDate(null);
  };

  // Calendar handlers
  const handleEventClick = (post: Post) => {
    setSelectedPost(post);
    setIsDetailModalOpen(true);
  };

  const handleEventDrop = async (postId: string, newDate: Date) => {
    await updatePost(postId, { scheduled_for: newDate.toISOString() });
  };

  const handleDateSelect = (start: Date) => {
    setDefaultScheduleDate(start);
    setIsModalOpen(true);
  };

  // Handle calendar context menu actions
  const handleCalendarAction = (action: CalendarAction) => {
    switch (action.type) {
      case 'new_post':
        setDefaultScheduleDate(action.date);
        setIsModalOpen(true);
        break;
      case 'new_flight':
        setFlightModalStartDate(action.date);
        setOpenFlightModal(true);
        break;
      case 'move_post':
        // Move existing post to the selected date
        if (action.post) {
          updatePost(action.post.id, { scheduled_for: action.date.toISOString() });
        }
        break;
    }
  };

  const handlePostUpdate = async (postId: string, data: { text?: string; scheduled_for?: string; platforms?: string[]; status?: 'draft' | 'scheduled'; post_type?: PostType }) => {
    await updatePost(postId, data);
  };

  const handlePostDelete = async (postId: string, options?: { deleteFromPlatforms?: boolean }) => {
    return await deletePost(postId, options);
  };

  const handlePostCancel = async (postId: string) => {
    await cancelPost(postId);
  };

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        }
        title={currentProject.name}
        subtitle={`/${currentProject.slug} • ${currentProject.owner_type === 'own' ? t('projects:ownProject') : t('projects:clientProject')}`}
        breadcrumbs={[{ label: t('common:nav.projects'), href: '/projects' }]}
        actions={
          <div className="flex items-center gap-4">
            {/* Report Button */}
            <button
              onClick={() => setIsReportModalOpen(true)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              title={t('projects:detail.report')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">{t('projects:detail.report')}</span>
            </button>

            {/* Stats */}
            {currentStats && (
              <div className="flex gap-4 text-center">
                <div>
                  <div className="text-xl font-bold text-gray-900">{currentStats.scheduledPosts}</div>
                  <div className="text-xs text-gray-500">{t('projects:detail.scheduled')}</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-green-600">{currentStats.publishedPosts}</div>
                  <div className="text-xs text-gray-500">{t('projects:detail.published')}</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-red-600">{currentStats.failedPosts}</div>
                  <div className="text-xs text-gray-500">{t('projects:detail.failed')}</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-600">{currentStats.socialAccounts}</div>
                  <div className="text-xs text-gray-500">{t('projects:detail.accounts')}</div>
                </div>
              </div>
            )}
          </div>
        }
      />

        {/* Flights Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <FlightsList
            projectId={id!}
            openCreateModal={openFlightModal}
            defaultStartDate={flightModalStartDate}
            onCreateModalClose={() => {
              setOpenFlightModal(false);
              setFlightModalStartDate(undefined);
            }}
          />
        </div>

        {/* Posts Section */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('projects:detail.posts')}</h2>

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                  viewMode === 'calendar'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('projects:detail.calendar')}
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                {t('projects:detail.list')}
              </button>
            </div>

          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/projects/${id}/creator`)}
              className="px-4 py-2 border border-purple-300 text-purple-700 rounded-md hover:bg-purple-50 flex items-center gap-2"
              title={t('projects:detail.creator')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('projects:detail.creator')}
            </button>
            <button
              onClick={() => setIsBatchModalOpen(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center gap-2"
              title={t('projects:detail.import')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {t('projects:detail.import')}
            </button>
            <button
              onClick={() => setIsBatchHistoryOpen(true)}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              title={t('projects:detail.import')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('projects:detail.newPost')}
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12 text-gray-500">{t('common:loading')}</div>
        ) : viewMode === 'calendar' ? (
          /* Calendar View */
          <PostCalendar
            posts={posts}
            flights={flights}
            onEventClick={handleEventClick}
            onFlightClick={(flight) => navigate(`/flights/${flight.id}`)}
            onEventDrop={handleEventDrop}
            onDateSelect={handleDateSelect}
            onCalendarAction={handleCalendarAction}
            currentView={calendarView}
            onViewChange={setCalendarView}
            showContextMenu={true}
          />
        ) : (
          /* List View */
          <PostListView
            posts={posts}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            onDelete={deletePost}
            onCancel={cancelPost}
            onClick={handleEventClick}
            onBatchUpdate={async (postIds, updates) => {
              await batchUpdate(postIds, updates);
              // Refetch posts after batch update
              fetchPosts(id!, { limit: 500 });
            }}
            onBatchDelete={async (postIds) => {
              await batchDelete(postIds);
              // Refetch posts after batch delete
              fetchPosts(id!, { limit: 500 });
            }}
            preferences={postListPrefs}
            onPreferencesChange={handlePreferencesChange}
          />
        )}

      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setDefaultScheduleDate(null);
        }}
        projectId={id!}
        onCreate={handleCreate}
        defaultDate={defaultScheduleDate}
        flights={flights}
      />

      <PostDetailModal
        post={selectedPost}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedPost(null);
        }}
        onUpdate={handlePostUpdate}
        onDelete={handlePostDelete}
        onCancel={handlePostCancel}
        onDuplicate={async (postId) => {
          await duplicatePost(postId);
          setIsDetailModalOpen(false);
          setSelectedPost(null);
          if (id) {
            fetchPosts(id, { limit: 500 });
          }
        }}
        flights={flights.map(f => ({ id: f.id, name: f.name, color: f.color || '#3B82F6' }))}
      />

      {isBatchModalOpen && (
        <BatchUpload
          projectId={id!}
          onImportComplete={() => {
            fetchPosts(id!, { limit: 500 });
            fetchProjectStats(id!);
          }}
          onClose={() => setIsBatchModalOpen(false)}
        />
      )}

      <BatchHistory
        projectId={id!}
        isOpen={isBatchHistoryOpen}
        onClose={() => setIsBatchHistoryOpen(false)}
        onBatchChange={() => {
          fetchPosts(id!, { limit: 500 });
          fetchProjectStats(id!);
        }}
      />

      <ReportGeneratorModal
        projectId={id!}
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
      />
    </div>
  );
}
